import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Navbar, Footer } from "@/components/Layout";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ContactPersonModal, ContactPerson } from "@/components/ContactPersonModal";
import { toast } from "sonner";
import { Facebook, Instagram, Linkedin, MessageCircle, TreePine, Camera, X, Pencil, Plus, AlertTriangle, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  slugifyUsername, isUsernameAvailable, isValidUsernameFormat,
  USERNAME_MIN, USERNAME_MAX,
} from "@/lib/usernameUtils";

interface ProfileData {
  full_name: string;
  username: string;
  phone: string;
  avatar_url: string | null;
  cover_image_url: string | null;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  whatsapp_number: string;
  skogsforum_url: string;
}

export default function EditProfile() {
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "RADERA") return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error) {
        // Try to read the structured error body returned by the function
        const ctx: any = (error as any).context;
        let body: any = null;
        try { body = ctx ? await ctx.json() : null; } catch { /* ignore */ }
        if (body?.error === "active_deals") {
          toast.error(body.message);
          setDeleting(false);
          return;
        }
        throw new Error(body?.error || error.message);
      }
      if ((data as any)?.error === "active_deals") {
        toast.error((data as any).message);
        setDeleting(false);
        return;
      }
      toast.success("Ditt konto har raderats.");
      await signOut();
      navigate("/", { replace: true });
    } catch (e: any) {
      toast.error(e?.message || "Kunde inte radera kontot.");
      setDeleting(false);
    }
  };

  const [profile, setProfile] = useState<ProfileData>({
    full_name: "",
    username: "",
    phone: "",
    avatar_url: null,
    cover_image_url: null,
    facebook_url: "",
    instagram_url: "",
    linkedin_url: "",
    whatsapp_number: "",
    skogsforum_url: "",
  });
  const [originalProfile, setOriginalProfile] = useState<ProfileData | null>(null);

  const [email, setEmail] = useState("");
  const [originalEmail, setOriginalEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [pendingChanges, setPendingChanges] = useState<any>(null);
  const [usernameStatus, setUsernameStatus] = useState<
    "idle" | "checking" | "available" | "taken" | "invalid"
  >("idle");

  // Debounced username availability check (skip when unchanged)
  useEffect(() => {
    if (!profile.username) { setUsernameStatus("idle"); return; }
    if (originalProfile && profile.username === originalProfile.username) {
      setUsernameStatus("idle"); return;
    }
    if (!isValidUsernameFormat(profile.username)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await isUsernameAvailable(profile.username);
      setUsernameStatus(ok ? "available" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [profile.username, originalProfile]);

  // Account type & contact persons (company only)
  const [accountType, setAccountType] = useState<"personal" | "company" | "admin" | null>(null);
  const [contactPersons, setContactPersons] = useState<ContactPerson[]>([]);
  const [originalContactIds, setOriginalContactIds] = useState<string[]>([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactPerson | null>(null);

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    fetchProfile();
    setEmail(user.email || "");
    setOriginalEmail(user.email || "");
  }, [user]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const fetchProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("full_name, username, phone, avatar_url, cover_image_url, facebook_url, instagram_url, linkedin_url, whatsapp_number, skogsforum_url, account_type")
      .eq("id", user.id)
      .single();
    if (data) {
      const p: ProfileData = {
        full_name: data.full_name || "",
        username: (data as any).username || "",
        phone: (data as any).phone || "",
        avatar_url: (data as any).avatar_url || null,
        cover_image_url: (data as any).cover_image_url || null,
        facebook_url: (data as any).facebook_url || "",
        instagram_url: (data as any).instagram_url || "",
        linkedin_url: (data as any).linkedin_url || "",
        whatsapp_number: (data as any).whatsapp_number || "",
        skogsforum_url: (data as any).skogsforum_url || "",
      };
      setProfile(p);
      setOriginalProfile(p);
      setAvatarPreview(p.avatar_url);
      setCoverPreview(p.cover_image_url);
      const aType = ((data as any).account_type ?? "personal") as "personal" | "company" | "admin";
      setAccountType(aType);

      if (aType === "company") {
        const { data: cps } = await supabase
          .from("company_contact_persons")
          .select("*")
          .eq("company_user_id", user.id)
          .order("position", { ascending: true });
        if (cps) {
          const mapped: ContactPerson[] = cps.map((c: any) => ({
            id: c.id,
            name: c.name,
            role: c.role ?? undefined,
            phone: c.phone ?? undefined,
            email: c.email ?? undefined,
            photo_url: c.photo_url ?? null,
          }));
          setContactPersons(mapped);
          setOriginalContactIds(mapped.map((m) => m.id));
        }
      }
    }
  };

  const upsertContact = (p: ContactPerson) => {
    setContactPersons((list) => {
      const exists = list.find((c) => c.id === p.id);
      if (exists) return list.map((c) => (c.id === p.id ? p : c));
      return [...list, p];
    });
  };

  const removeContact = (id: string) => {
    if (!confirm("Ta bort kontaktperson?")) return;
    setContactPersons((list) => list.filter((c) => c.id !== id));
  };

  const persistContactPersons = async () => {
    if (!user || accountType !== "company") return;
    const currentIds = new Set(contactPersons.map((c) => c.id));
    const toDelete = originalContactIds.filter((id) => !currentIds.has(id));
    if (toDelete.length) {
      await supabase.from("company_contact_persons").delete().in("id", toDelete);
    }
    const rows = await Promise.all(
      contactPersons.map(async (cp, i) => {
        let photo_url: string | null = cp.photo_url ?? null;
        if (cp._file) {
          const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
          if (!allowed.includes(cp._file.type)) {
            throw new Error("Endast bilder (JPG, PNG, WEBP, GIF) tillåts för kontaktpersoner.");
          }
          const safeExt = cp._file.type === "image/jpeg" ? "jpg"
            : cp._file.type === "image/png" ? "png"
            : cp._file.type === "image/webp" ? "webp"
            : "gif";
          const path = `${user.id}/contacts/${cp.id}.${safeExt}`;
          const { error: upErr } = await supabase.storage
            .from("avatars")
            .upload(path, cp._file, { upsert: true, contentType: cp._file.type });
          if (!upErr) {
            photo_url = supabase.storage.from("avatars").getPublicUrl(path).data.publicUrl;
          }
        }
        return {
          id: cp.id,
          company_user_id: user.id,
          name: cp.name,
          role: cp.role ?? null,
          phone: cp.phone ?? null,
          email: cp.email ?? null,
          photo_url,
          position: i,
        };
      })
    );
    if (rows.length) {
      const { error } = await supabase
        .from("company_contact_persons")
        .upsert(rows, { onConflict: "id" });
      if (error) throw error;
    }
    setOriginalContactIds(rows.map((r) => r.id));
    setContactPersons((list) =>
      list.map((c) => ({
        ...c,
        _file: null,
        _previewUrl: null,
        photo_url: rows.find((r) => r.id === c.id)?.photo_url ?? c.photo_url,
      }))
    );
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const hasSensitiveChanges = () => {
    if (!originalProfile) return false;
    return (
      profile.full_name !== originalProfile.full_name ||
      profile.phone !== originalProfile.phone ||
      email !== originalEmail ||
      password.length > 0
    );
  };

  const completeSocialUrl = (value: string, platform: string): string | null => {
    if (!value) return null;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    const baseUrls: Record<string, string> = {
      facebook: "https://www.facebook.com/",
      instagram: "https://www.instagram.com/",
      linkedin: "https://www.linkedin.com/in/",
      whatsapp: "https://wa.me/",
      skogsforum: "https://www.skogsforum.se/members/",
    };
    const handle = platform === "whatsapp" ? value.replace(/[\s-]/g, "") : value;
    return baseUrls[platform] + handle;
  };

  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

  const uploadImage = async (file: File, path: string) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error("Endast bilder (JPG, PNG, WEBP, GIF) tillåts.");
    }
    // Force a safe extension based on the verified MIME type, ignoring filename.
    const safeExt = file.type === "image/jpeg" ? "jpg"
      : file.type === "image/png" ? "png"
      : file.type === "image/webp" ? "webp"
      : "gif";
    const safePath = path.replace(/\.[^./]+$/, "") + "." + safeExt;

    // Clean up any previous files with the same basename but different extensions
    // (e.g. old avatar.png when uploading avatar.jpg). Folder = everything before last "/".
    const lastSlash = safePath.lastIndexOf("/");
    const folder = lastSlash >= 0 ? safePath.slice(0, lastSlash) : "";
    const newFileName = safePath.slice(lastSlash + 1);
    const baseName = newFileName.replace(/\.[^.]+$/, "");
    try {
      const { data: existing } = await supabase.storage.from("avatars").list(folder, { limit: 100 });
      if (existing && existing.length > 0) {
        const toRemove = existing
          .filter((f) => f.name !== newFileName && f.name.replace(/\.[^.]+$/, "") === baseName)
          .map((f) => (folder ? `${folder}/${f.name}` : f.name));
        if (toRemove.length > 0) {
          await supabase.storage.from("avatars").remove(toRemove);
        }
      }
    } catch {
      // Non-fatal: proceed with upload even if cleanup listing fails.
    }

    const { error } = await supabase.storage.from("avatars").upload(safePath, file, {
      upsert: true,
      contentType: file.type,
    });
    if (error) throw error;
    const { data } = supabase.storage.from("avatars").getPublicUrl(safePath);
    return data.publicUrl;
  };

  const sendVerificationCode = async () => {
    const { error } = await supabase.functions.invoke("send-verification-code", {
      body: { action: "send" },
    });
    if (error) {
      toast.error("Kunde inte skicka verifieringskod");
      return false;
    }
    setCooldown(30);
    return true;
  };

  const handleSave = async () => {
    if (password && password !== confirmPassword) {
      setPasswordError("Lösenorden matchar inte.");
      return;
    }
    setPasswordError("");

    // Validate username before any save path
    const usernameChanged = !!originalProfile && profile.username !== originalProfile.username;
    if (usernameChanged) {
      if (!isValidUsernameFormat(profile.username)) {
        toast.error("Ogiltigt användarnamn (endast a–z, 0–9 och bindestreck, minst 2 tecken).");
        return;
      }
      if (usernameStatus === "taken") {
        toast.error("Det här användarnamnet är redan taget.");
        return;
      }
      const stillFree = await isUsernameAvailable(profile.username);
      if (!stillFree) {
        setUsernameStatus("taken");
        toast.error("Det här användarnamnet är redan taget.");
        return;
      }
    }

    if (hasSensitiveChanges()) {
      setPendingChanges({
        sensitive: true,
        full_name: profile.full_name,
        phone: profile.phone,
        email: email !== originalEmail ? email : undefined,
        password: password || undefined,
      });
      const sent = await sendVerificationCode();
      if (sent) setShowVerification(true);
    } else {
      await applyNonSensitiveChanges();
    }
  };

  const applyNonSensitiveChanges = async () => {
    setSaving(true);
    try {
      let avatarUrl = profile.avatar_url;
      let coverUrl = profile.cover_image_url;

      if (avatarFile && user) {
        const ext = avatarFile.name.split(".").pop();
        avatarUrl = await uploadImage(avatarFile, `${user.id}/avatar.${ext}`);
      }
      if (coverFile && user) {
        const ext = coverFile.name.split(".").pop();
        coverUrl = await uploadImage(coverFile, `${user.id}/cover.${ext}`);
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          username: profile.username,
          avatar_url: avatarUrl,
          cover_image_url: coverUrl,
          facebook_url: completeSocialUrl(profile.facebook_url, "facebook"),
          instagram_url: completeSocialUrl(profile.instagram_url, "instagram"),
          linkedin_url: completeSocialUrl(profile.linkedin_url, "linkedin"),
          whatsapp_number: profile.whatsapp_number || null,
          skogsforum_url: completeSocialUrl(profile.skogsforum_url, "skogsforum"),
        } as any)
        .eq("id", user!.id);

      if (error) throw error;
      await persistContactPersons();
      toast.success("Dina ändringar har sparats.");
      setAvatarFile(null);
      setCoverFile(null);
    } catch (err: any) {
      toast.error(err.message || "Kunde inte spara ändringar");
    } finally {
      setSaving(false);
    }
  };

  const applyAllChanges = async () => {
    setSaving(true);
    try {
      let avatarUrl = profile.avatar_url;
      let coverUrl = profile.cover_image_url;

      if (avatarFile && user) {
        const ext = avatarFile.name.split(".").pop();
        avatarUrl = await uploadImage(avatarFile, `${user.id}/avatar.${ext}`);
      }
      if (coverFile && user) {
        const ext = coverFile.name.split(".").pop();
        coverUrl = await uploadImage(coverFile, `${user.id}/cover.${ext}`);
      }

      // Update profile table
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name,
          username: profile.username,
          phone: profile.phone || null,
          avatar_url: avatarUrl,
          cover_image_url: coverUrl,
          facebook_url: completeSocialUrl(profile.facebook_url, "facebook"),
          instagram_url: completeSocialUrl(profile.instagram_url, "instagram"),
          linkedin_url: completeSocialUrl(profile.linkedin_url, "linkedin"),
          whatsapp_number: profile.whatsapp_number || null,
          skogsforum_url: completeSocialUrl(profile.skogsforum_url, "skogsforum"),
        } as any)
        .eq("id", user!.id);

      if (profileError) throw profileError;

      // Update email if changed
      if (pendingChanges?.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email: pendingChanges.email });
        if (emailError) throw emailError;
      }

      // Update password if changed
      if (pendingChanges?.password) {
        const { error: pwError } = await supabase.auth.updateUser({ password: pendingChanges.password });
        if (pwError) throw pwError;
      }

      await persistContactPersons();
      toast.success("Dina ändringar har sparats.");
      setAvatarFile(null);
      setCoverFile(null);
      setPassword("");
      setConfirmPassword("");
      setPendingChanges(null);
      // Refresh profile
      await fetchProfile();
      setOriginalEmail(pendingChanges?.email || originalEmail);
    } catch (err: any) {
      toast.error(err.message || "Kunde inte spara ändringar");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerificationError("");
    const { data, error } = await supabase.functions.invoke("send-verification-code", {
      body: { action: "verify", code: verificationCode },
    });
    if (error || !data?.valid) {
      setVerificationError("Felaktig kod. Försök igen.");
      setVerificationCode("");
      return;
    }
    setShowVerification(false);
    setVerificationCode("");
    await applyAllChanges();
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    await sendVerificationCode();
  };

  const initials = profile.full_name
    ? profile.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container max-w-2xl flex-1 py-8">
        <BackButton fallback="/dashboard" />
        <h1 className="mb-6 text-2xl font-bold">Redigera profil</h1>

        {/* Container 1 – Profilbilder */}
        <div className="mb-6 rounded-xl bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold">Profilbilder</h2>

          {/* Avatar */}
          <div className="mb-6 flex flex-col items-center">
            <Label className="mb-2 text-sm text-muted-foreground">Profilbild</Label>
            <Avatar className="h-24 w-24 mb-3">
              {avatarPreview ? (
                <AvatarImage src={avatarPreview} alt="Profilbild" />
              ) : (
                <AvatarFallback className="text-xl">{initials}</AvatarFallback>
              )}
            </Avatar>
            <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
            <Button variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()}>
              <Camera className="mr-2 h-4 w-4" /> Ändra bild
            </Button>
          </div>

          {/* Cover image */}
          <div className="flex flex-col items-center">
            <Label className="mb-2 text-sm text-muted-foreground">Omslagsbild</Label>
            <div
              className="mb-3 w-full h-32 rounded-lg bg-muted flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={() => coverInputRef.current?.click()}
            >
              {coverPreview ? (
                <img src={coverPreview} alt="Omslagsbild" className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm text-muted-foreground">Ingen omslagsbild</span>
              )}
            </div>
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverChange} />
            <Button variant="outline" size="sm" onClick={() => coverInputRef.current?.click()}>
              <Camera className="mr-2 h-4 w-4" /> Ändra omslagsbild
            </Button>
          </div>
        </div>

        {/* Container 2 – Kontoinformation */}
        <div className="mb-6 rounded-xl bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold">Kontoinformation</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">Namn</Label>
              <Input
                id="full_name"
                value={profile.full_name}
                onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="username">Användarnamn</Label>
              <div className="relative">
                <Input
                  id="username"
                  value={profile.username}
                  onChange={e => setProfile(p => ({ ...p, username: slugifyUsername(e.target.value) }))}
                  placeholder="ditt-anvandarnamn"
                  minLength={USERNAME_MIN}
                  maxLength={USERNAME_MAX}
                  autoCapitalize="none"
                  autoComplete="off"
                  className={cn(
                    usernameStatus === "available" && "border-primary focus-visible:ring-primary/40",
                    (usernameStatus === "taken" || usernameStatus === "invalid") && "border-destructive focus-visible:ring-destructive/40",
                  )}
                />
                {usernameStatus === "available" && (
                  <Check className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                )}
              </div>
              {usernameStatus === "checking" && (
                <p className="mt-1 text-xs text-muted-foreground">Kontrollerar tillgänglighet…</p>
              )}
              {usernameStatus === "available" && (
                <p className="mt-1 text-xs text-primary">Användarnamnet är tillgängligt.</p>
              )}
              {usernameStatus === "taken" && (
                <p className="mt-1 text-xs text-destructive">Det här användarnamnet är redan taget.</p>
              )}
              {usernameStatus === "invalid" && (
                <p className="mt-1 text-xs text-destructive">Endast små bokstäver, siffror och bindestreck (minst 2 tecken).</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Din profil-URL: timbernet.se/saljare/{profile.username || "..."}
              </p>
            </div>
            <div>
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="phone">Telefonnummer</Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="password">Lösenord</Label>
              <Input
                id="password"
                type="password"
                placeholder="Ange nytt lösenord"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            {password.length > 0 && (
              <div>
                <Label htmlFor="confirm_password">Bekräfta lösenord</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Bekräfta nytt lösenord"
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                    setPasswordError("");
                  }}
                />
                {passwordError && <p className="mt-1 text-sm text-destructive">{passwordError}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Container 3 – Sociala länkar */}
        <div className="mb-6 rounded-xl bg-card p-6">
          <h2 className="mb-4 text-lg font-semibold">Sociala länkar</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Facebook className="h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                placeholder="https://facebook.com/ditt-namn"
                value={profile.facebook_url}
                onChange={e => setProfile(p => ({ ...p, facebook_url: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <Instagram className="h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                placeholder="https://instagram.com/ditt-namn"
                value={profile.instagram_url}
                onChange={e => setProfile(p => ({ ...p, instagram_url: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <Linkedin className="h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                placeholder="https://linkedin.com/in/ditt-namn"
                value={profile.linkedin_url}
                onChange={e => setProfile(p => ({ ...p, linkedin_url: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                placeholder="Telefonnummer kopplat till WhatsApp"
                value={profile.whatsapp_number}
                onChange={e => setProfile(p => ({ ...p, whatsapp_number: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <TreePine className="h-5 w-5 shrink-0 text-muted-foreground" />
              <Input
                placeholder="https://skogsforum.se/members/ditt-namn"
                value={profile.skogsforum_url}
                onChange={e => setProfile(p => ({ ...p, skogsforum_url: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Container 4 – Kontaktpersoner (företag) */}
        {accountType === "company" && (
          <div className="mb-6 rounded-xl bg-card p-6">
            <h2 className="mb-1 text-lg font-semibold">Kontaktpersoner</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Kontaktpersoner som visas under fliken "Personal" på företagets profil.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {contactPersons.map((p) => {
                const initials = p.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2);
                return (
                  <div
                    key={p.id}
                    className="relative flex items-center gap-3 rounded-lg border border-border bg-background p-3"
                  >
                    <Avatar className="h-12 w-12">
                      {p._previewUrl || p.photo_url ? (
                        <AvatarImage src={p._previewUrl ?? p.photo_url ?? undefined} alt={p.name} />
                      ) : (
                        <AvatarFallback>{initials || "?"}</AvatarFallback>
                      )}
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{p.name}</p>
                      {p.role && <p className="truncate text-xs text-muted-foreground">{p.role}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => { setEditingContact(p); setContactModalOpen(true); }}
                        className="rounded p-1 text-muted-foreground hover:bg-muted"
                        aria-label="Redigera"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeContact(p.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                        aria-label="Ta bort"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-3"
              onClick={() => { setEditingContact(null); setContactModalOpen(true); }}
            >
              <Plus className="mr-1 h-4 w-4" /> Lägg till person
            </Button>
          </div>
        )}

        {/* Save button */}
        <Button
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          size="lg"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Sparar..." : "Spara ändringar"}
        </Button>

        {/* Danger zone */}
        <div className="mt-12 rounded-lg border border-destructive/40 bg-destructive/5 p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground">Radera ditt konto</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                När du raderar ditt konto tas all din profilinformation, dina annonser, tjänster och sparade områden bort permanent. Mottagna omdömen raderas. Chattar du varit del av döljs på din sida — din motpart ser dem tills även de raderar tråden.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Du kan inte radera ditt konto om du har pågående köp, försäljningar eller bokningar.
              </p>
              <Button
                variant="destructive"
                className="mt-4"
                onClick={() => { setDeleteOpen(true); setDeleteConfirm(""); }}
              >
                Radera mitt konto
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={(open) => {
        if (!deleting) {
          setDeleteOpen(open);
          if (!open) setDeleteConfirm("");
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bekräfta radering</DialogTitle>
            <DialogDescription>
              Detta går inte att ångra. Skriv <span className="font-mono font-semibold text-foreground">RADERA</span> nedan för att bekräfta att du vill radera ditt konto permanent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              autoFocus
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="RADERA"
              disabled={deleting}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
              >
                Avbryt
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== "RADERA" || deleting}
              >
                {deleting ? "Raderar..." : "Radera permanent"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verification Modal */}
      <Dialog open={showVerification} onOpenChange={(open) => {
        if (!open) {
          setShowVerification(false);
          setVerificationCode("");
          setVerificationError("");
          setPendingChanges(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Verifiera din identitet</DialogTitle>
            <DialogDescription>
              En sexsiffrig kod har skickats till {originalEmail}. Ange koden nedan för att bekräfta dina ändringar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
            {verificationError && (
              <p className="text-sm text-destructive">{verificationError}</p>
            )}
            <Button
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={verificationCode.length < 6 || saving}
              onClick={handleVerify}
            >
              {saving ? "Sparar..." : "Bekräfta"}
            </Button>
            <button
              className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              disabled={cooldown > 0}
              onClick={handleResend}
            >
              {cooldown > 0 ? `Skicka koden igen (${cooldown}s)` : "Skicka koden igen"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ContactPersonModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSave={upsertContact}
        initial={editingContact}
      />

      <Footer hideOnMobile />
    </div>
  );
}
